import { useState, useEffect, useCallback } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './Submenu';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { TimekeeperService } from 'services/TimekeeperService';

const tk = new TimekeeperService();

const HolidayTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<any>([]);

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('settings.holidays.table.columns.name'), flex: 1, type: 'string', filterable: true },
        { field: 'public_name', headerName: t('settings.holidays.table.columns.visualization_name'), flex: 1, type: 'string', filterable: true },
        {
            field: 'start_date',
            headerName: t('settings.holidays.table.columns.start'),
            flex: 1,
            type: 'string',
            filterable: false,
            renderCell: (params) => {
                return <div>{dayjs.utc(params.value).format('L HH:mm')}</div>;
            },
        },
        {
            field: 'end_date',
            headerName: t('settings.holidays.table.columns.end'),
            flex: 1,
            type: 'string',
            filterable: false,
            renderCell: (params) => {
                return <div>{dayjs.utc(params.value).format('L HH:mm')}</div>;
            },
        },
    ];

    const reloading = useCallback(() => {
        setLoading(true);
        tk.holiday
            .getHolidays()
            .then((response) => {
                const holidays = response.map((holiday) => ({
                    id: holiday.holiday_id,
                    name: holiday.name,
                    public_name: holiday.public_name,
                    year: holiday.year,
                    is_renounceable: holiday.is_renounceable,
                    is_local: holiday.is_local,
                    country: holiday.country,
                    start_date: dayjs.utc(holiday.start).local(),
                    end_date: dayjs.utc(holiday.end).local(),
                }));
                const sortedHolidays = holidays.sort((a, b) => a.name.localeCompare(b.name));
                setRows(sortedHolidays);
            })
            .catch((error) => {
                console.error('Error al obtener los feriados:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const cleanSelected = () => {
        setSelectedHoliday([]);
        setSelected([]);
    };

    useEffect(() => {
        reloading();
    }, [reloading]);

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                rowSelectionModel={selectedHoliday}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let row = rows.filter((row) => newRowSelectionModel.includes(row.id));
                    setSelectedHoliday(newRowSelectionModel);
                    setSelected(row);
                }}
                checkboxSelection
                localeText={{
                    headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                    headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                    headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                    headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                    headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                    headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                    footerRowSelected: (count) => (count > 1 ? `${count.toLocaleString()} filas seleccionadas` : `${count.toLocaleString()} fila seleccionada`),

                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                }}
            />
            <SubMenu selected={selected} setSelected={cleanSelected} rows={rows} contextMenu={contextMenu} setContextMenu={setContextMenu} reloading={reloading} />
        </div>
    );
};

export { HolidayTable };
